
import { Component, Vue, Prop } from 'vue-property-decorator';
import { ErrorHandler } from '../../helpers/errorHandler';
import { Transaction } from '../../models/Transaction';
import { PricingPlans } from '../../helpers/pricingPlan';
import { useBraintree } from '../../composables/useBraintree';

@Component({ })
export default class ChangeCreditCardDialog extends Vue {
    @Prop(Boolean) public showDialog!: boolean;
    private showAmex: boolean = true;
    private isLoading: boolean = true;
    private actionLoading: boolean = false;
    private token: string = '';
    private changeStatus: string = '';
    private rules: any = {
        required: (value) => !!value || 'Required.',
    };

    private errorCreditCardNumber: boolean = false;
    private errorCardholderName: boolean = false;
    private errorExpirationDate: boolean = false;
    private errorCvv: boolean = false;
    private errorGeneral: boolean = false;
    private errorMessageCreditCardNumber: string = '';
    private errorMessageCardholderName: string = '';
    private errorMessageExpirationDate: string = '';
    private errorMessageCvv: string = '';
    private errorMessageGeneral: string = '';

    private clientInstance: any = null;
    private threeDSecureInstance: any = null;
    private braintree = useBraintree();

    private pricingPlans = new PricingPlans();
    private subscriptionPrice: string = '0';

    private transaction: Transaction = {
        cardholderName: '',
        pricingPlan: this.pricingPlans.Basic.type.toLowerCase(),
        paymentMethodNonce: '',
        creditCardNumber: '',
        expirationDate: '',
        cvv: '',
        newCard: true,
        imageUrl: '',
        trialDuration: 0
    };

    private userEmail: string = '';

    private async created() {
        this.changeStatus = '';
        await this.generateToken();
        try {
            // @ts-ignore
            this.axios.get(`https://ipinfo.io/json?token=81a40b334a509c`).then(response => {
                if (response.data.country === 'US' || response.data.country === 'USA') {
                    this.showAmex = false;
                }
            });
        } catch(error: any) {
            new ErrorHandler().Server(error);
        }
    }

    private closeDialog() {
        // @ts-ignore
        if (this.$refs.form) {
            // @ts-ignore
            this.$refs.form.reset();
        }
        this.$emit('update:showDialog', false);
    }

    private async generateToken() {
        try {
            // @ts-ignore
            const response = await this.axios.get(`/api/braintree/GenerateToken`);
            this.token = response.data.data;
            await this.createBraintreeInstance();
        } catch (error: any) {
            new ErrorHandler().Server(error);
        }
    }

    private async createBraintreeInstance() {
        try {
            this.clientInstance = await this.braintree.initClient(this.token);
            this.transaction.deviceData = await this.braintree.initDataCollector(this.clientInstance);
            this.threeDSecureInstance = await this.braintree.initThreeDSecure(this.clientInstance);
            this.isLoading = false;
        } catch (error: any) {
            alert(error);
        }
    }

    private async changeCreditCard() {
        this.resetErrors();
        // @ts-ignore
        if (this.$refs.form && this.$refs.form.validate() === false) {
            return;
        }
        if (!this.transaction.cardholderName) {
            this.errorCardholderName = true;
            this.errorMessageCardholderName = 'Required.';
            return;
        }

        this.actionLoading = true;

        try {
            let expirationDate = this.transaction.expirationDate;
            if (expirationDate && expirationDate.indexOf('/') === -1) {
                expirationDate = expirationDate.substring(0, 2) + '/' + expirationDate.substring(2, 4);
            }

            const tokenized = await this.braintree.tokenizeRawCard(this.clientInstance, {
                number: this.transaction.creditCardNumber,
                expirationDate,
                cvv: this.transaction.cvv,
                cardholderName: this.transaction.cardholderName,
            });

            const verifyResult = await this.braintree.verify3DSecure(this.threeDSecureInstance, {
                nonce: tokenized.nonce,
                bin: tokenized.bin,
                amount: this.subscriptionPrice,
                email: this.userEmail,
                cardholderName: this.transaction.cardholderName,
                challengeRequested: true,
                collectDeviceData: true,
            });

            const evaluation = this.braintree.evaluate3DSResult(verifyResult);
            if (!evaluation.accepted) {
                this.errorGeneral = true;
                this.errorMessageGeneral = 'Your bank could not verify your identity. Please try again or use a different card.';
                this.actionLoading = false;
                return;
            }

            await this.createPayment(verifyResult.nonce, verifyResult.threeDSecureAuthenticationId);
        } catch (error: any) {
            this.actionLoading = false;
            if (error && error.details && error.details.originalError && error.details.originalError.fieldErrors) {
                error.details.originalError.fieldErrors.forEach((err: any) => {
                    err.fieldErrors.forEach((fieldError: any) => {
                        switch (fieldError.field) {
                            case 'number':
                                this.errorCreditCardNumber = true;
                                this.errorMessageCreditCardNumber = fieldError.message;
                                break;
                            case 'expirationMonth':
                                this.errorExpirationDate = true;
                                this.errorMessageExpirationDate = fieldError.message;
                                break;
                            case 'cvv':
                                this.errorCvv = true;
                                this.errorMessageCvv = fieldError.message;
                                break;
                        }
                    });
                });
            } else {
                this.errorGeneral = true;
                this.errorMessageGeneral = error && error.message ? error.message : 'An error occurred. Please try again.';
            }
        }
    }

    private async createPayment(nonce: string, threeDSecureAuthenticationId: string | null) {
        const payment: object = {
            paymentMethodNonce: nonce,
            threeDSecureAuthenticationId,
            newCard: this.transaction.newCard,
            pricingPlan: this.transaction.pricingPlan,
            cardholderName: this.transaction.cardholderName,
            creditCardNumber: this.transaction.creditCardNumber,
            deviceData: this.transaction.deviceData,
        };
        try {
            // @ts-ignore
            await this.axios.post<Transaction>(`/api/braintree/changecreditcard`, payment);
            this.changeStatus = 'success';
            if (this.clientInstance) {
                this.clientInstance.teardown();
            }
        } catch (error: any) {
            new ErrorHandler().Server(error);
            this.errorCreditCardNumber = true;
            this.errorMessageCreditCardNumber = new ErrorHandler().errorMessage;
        } finally {
            this.actionLoading = false;
        }
    }

    private resetErrors() {
        this.errorCreditCardNumber = false;
        this.errorCardholderName = false;
        this.errorExpirationDate = false;
        this.errorCvv = false;
        this.errorGeneral = false;
        this.errorMessageCreditCardNumber = '';
        this.errorMessageCardholderName = '';
        this.errorMessageExpirationDate = '';
        this.errorMessageCvv = '';
        this.errorMessageGeneral = '';
    }
}
